import React from 'react';
import { motion } from 'framer-motion';
import { UsersIcon, GlobeIcon, ShieldIcon, FolderIcon, CompassIcon, MessageSquareIcon, CheckCircle2Icon } from 'lucide-react';
import { Container } from '../components/ui/Container';
import { Button } from '../components/ui/Button';
import { PathCard } from '../components/ui/PathCard';
import { GeometricAccent } from '../components/ui/GeometricAccent';
import { PatternDecor } from '../components/ui/PatternDecor';
import { MosqueArchMask } from '../components/ui/MosqueArchMask';
import { Link } from 'react-router-dom';
export function Parents() {
  return <>
      {/* Hero */}
      <section className="relative overflow-hidden isolate" data-id="element-3976" data-mp="2ge4ffrncp8i7">
        <PatternDecor side="right" y="middle" size="lg" data-id="element-3977" data-mp="2kdd78qytywb4" />
        <GeometricAccent variant="dots" className="absolute top-8 right-8 w-32 h-32" opacity={0.4} data-id="element-3978" data-mp="2kg7ueq98av88" />
        <Container className="relative pt-8 md:pt-12 pb-16 md:pb-24" data-id="element-3979" data-mp="ina2cwna06oc">
          <div className="grid lg:grid-cols-12 gap-10 items-center" data-id="element-3980" data-mp="3nx2sbyspsixg">
            <div className="lg:col-span-6" data-id="element-3981" data-mp="3nv9kwhaa1uoe">
              <span className="text-forest-700 text-sm font-semibold" data-id="element-3982" data-mp="3iy9gsbwzn1zx">
                Supporting parents. Protecting children. Strengthening families.
              </span>
              <h1 className="font-display text-5xl md:text-7xl text-ink-900 mt-3 leading-tight" data-id="element-3983" data-mp="1jcdoj4a6szm">
                Parents
              </h1>
              <p className="mt-6 text-ink-700 max-w-xl leading-relaxed" data-id="element-3984" data-mp="34xf3pfyzwcux">
                Parents play a critical role in how Muslim children grow,
                integrate, and feel secure in non-Muslim countries.
              </p>
              <p className="mt-3 text-ink-700 max-w-xl leading-relaxed" data-id="element-3985" data-mp="3exz7oqdwfvgu">
                Our parent programs are designed to{' '}
                <strong data-id="element-3986" data-mp="39znvi904t8vg">support, guide, and empower parents</strong> — giving
                them clarity, confidence, and practical tools for real-life
                challenges.
              </p>
              <p className="mt-3 text-ink-700 max-w-xl leading-relaxed" data-id="element-3987" data-mp="p9f5cy4a9wxp">
                This is not parenting advice from theory. It is{' '}
                <strong data-id="element-3988" data-mp="118c2rsjux801">guided support rooted in Islamic principles and lived experience.</strong>
              </p>
              <div className="mt-8 flex flex-wrap gap-3" data-id="element-3989" data-mp="169rkpbqqmye7">
                <Link to="/en/parents/membership" data-id="element-3990" data-mp="1b79q4s5jqvhg">
                  <Button variant="primary" data-id="element-3991" data-mp="1b6runxqpklal">Explore Membership</Button>
                </Link>
                <Link to="/en/parents/resources-guidance" data-id="element-3992" data-mp="dlccp960xgcd">
                  <Button variant="secondary" data-id="element-3993" data-mp="iichzhv1uf2f">View Resources</Button>
                </Link>
              </div>
            </div>
            <motion.div initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.7
          }} className="lg:col-span-6 relative" data-id="element-3994" data-mp="nh6z8fddbvkf">
              <div className="relative w-full lg:w-[calc(100%+6rem)] lg:-ml-8 aspect-[200/148]" data-id="element-3995" data-mp="2d2dzqcidefow">
                <MosqueArchMask src="https://cdn.magicpatterns.com/uploads/eG2z3gWLjme6SMefkcRt94/Parents6.jpg" alt="Muslim family with two children standing together at home" data-id="element-3996" data-mp="291c03ukjk1me" />
              </div>
            </motion.div>
          </div>
        </Container>
      </section>

      {/* Choose path */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-sage-50/60" data-id="element-3997" data-mp="1u5cuqrotgfky">
        <PatternDecor side="left" y="top" size="lg" data-id="element-3998" data-mp="1pc9z8e0haw24" />
        <Container data-id="element-3999" data-mp="1lb88ymojhuca">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-4000" data-mp="1laoqn4uz93xq">
            Choose your path
          </h2>
          <div className="mt-10 grid md:grid-cols-2 gap-5" data-id="element-4001" data-mp="1gdycwo6nakfh">
            <PathCard icon={UsersIcon} tone="sage" title="Parent Support Membership" description="The Parent Membership provides ongoing guidance, resources, and expert support for Muslim parents raising children in non-Muslim societies." linkLabel="Explore Membership" href="/en/parents/membership" data-id="element-4002" data-mp="1bdtrcjqvqzie" />
            <PathCard icon={GlobeIcon} tone="sky" title="Understanding the Environment Your Child Grows In" description="Raising Muslim children in non-Muslim countries requires awareness, preparation, and emotional strength." linkLabel="Learn more" href="/en/parents/parenting-in-non-muslim-countries" data-id="element-4003" data-mp="2wr6yqpuj2h84" />
            <PathCard icon={ShieldIcon} tone="rose" title="Protection, Awareness & Calm Response" description="Discrimination is often subtle — and difficult for children to explain. The focus is on prevention, dignity, and resolution, not confrontation." linkLabel="Learn more" href="/en/parents/preventing-discrimination" data-id="element-4004" data-mp="2dsb7nndyuiyi" />
            <PathCard icon={FolderIcon} tone="sand" title="Practical Support for Everyday Parenting" description="Parents receive access to carefully curated resources designed for Muslim families living in non-Muslim environments." linkLabel="View resources" href="/en/parents/resources-guidance" data-id="element-4005" data-mp="21ibqka9ka5bl" />
          </div>
        </Container>
      </section>

      {/* Support system */}
      <section className="py-16 md:py-24 bg-white/60" data-id="element-4006" data-mp="2g6zj07o41yxb">
        <Container data-id="element-4007" data-mp="2g8ixbsq7hzwg">
          <div className="max-w-2xl mx-auto text-center" data-id="element-4008" data-mp="2u67hszgd350z">
            <h2 className="font-display text-3xl md:text-4xl text-ink-900 leading-tight" data-id="element-4009" data-mp="2u5ph9qmo24n9">
              A support system — not a course
            </h2>
            <p className="mt-5 text-ink-700 leading-relaxed" data-id="element-4010" data-mp="zroh92fdrbll">
              Membership helps parents understand challenges their children
              face, respond calmly and confidently, protect their family values,
              and avoid isolation and confusion.
            </p>
            <p className="mt-4 text-ink-900 font-semibold" data-id="element-4011" data-mp="2zufyehqincr8">
              This is a support system, not a course.
            </p>
            <div className="mt-8 flex justify-center" data-id="element-4012" data-mp="33vro0n9e3xzf">
              <Link to="/en/waitlist" data-id="element-4013" data-mp="33ydxisfucnka">
                <Button variant="primary" data-id="element-4014" data-mp="38ve20yx1li9a">Join Membership</Button>
              </Link>
            </div>
            <p className="mt-4 text-sm text-ink-500" data-id="element-4015" data-mp="3dv0dp3x82qfz">
              New content monthly • Recordings included • Cancel anytime
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-5 items-start" data-id="element-4016" data-mp="3iqz22rijw8ge">
            <div className="rounded-2xl bg-white border border-sage-200 p-6 md:p-7 shadow-soft" data-id="element-4017" data-mp="3nktr8agxdfmv">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-4" data-id="element-4018" data-mp="3rku5nv40xqtx">
                Included with Membership
              </p>
              <ul className="space-y-2.5 text-ink-700" data-id="element-4019" data-mp="5dm7bcriuk7e">
                {['monthly expert sessions', 'parent-only discussions', 'practical guides & resources', 'priority support & updates'].map(c => <li key={c} className="flex items-center gap-3" data-id="element-4020" data-mp="5jefa06qd2l8">
                    <CheckCircle2Icon size={16} className="text-forest-700 flex-shrink-0" strokeWidth={1.6} data-id="element-4021" data-mp="1ikqz5pdllrt" />
                    <span data-id="element-4022" data-mp="t9qulw4b21lu">{c}</span>
                  </li>)}
              </ul>
            </div>
            <div className="rounded-2xl bg-white border border-sage-200 p-6 md:p-7 shadow-soft" data-id="element-4023" data-mp="oisbkorxmpew">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-4" data-id="element-4024" data-mp="khowi4hpkxeq">
                Best for parents who want to
              </p>
              <ul className="space-y-2.5 text-ink-700" data-id="element-4025" data-mp="1h1fsrt3t5kap">
                {['respond calmly to difficult situations at school', 'protect identity without isolation', 'communicate confidently with institutions', 'support children through sensitive questions'].map(c => <li key={c} className="flex items-center gap-3" data-id="element-4026" data-mp="1lziivt1g8ngz">
                    <CheckCircle2Icon size={16} className="text-forest-700 flex-shrink-0" strokeWidth={1.6} data-id="element-4027" data-mp="1qxalpi3mttzy" />
                    <span data-id="element-4028" data-mp="2jly1wz9ixt6n">{c}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </Container>
      </section>

      {/* How it works */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-[#EDF2F7]/60" data-id="element-4029" data-mp="29o1scbw8j6an">
        <PatternDecor side="right" y="bottom" size="md" data-id="element-4030" data-mp="29pv2i16248ne" />
        <Container data-id="element-4031" data-mp="t90jp33uj68u">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-4032" data-mp="t8h1dlaaafua">
            How it works
          </h2>
          <div className="mt-10 grid md:grid-cols-3 gap-5" data-id="element-4033" data-mp="3cgnqzmh7k2bn">
            {[{
            icon: CompassIcon,
            title: 'Choose a topic',
            text: 'Start with membership, parenting guidance, discrimination prevention, or practical resources — based on what your family needs now.',
            bg: 'bg-sage-100'
          }, {
            icon: MessageSquareIcon,
            title: 'Get guided support',
            text: 'Join sessions, explore materials, and learn calm, practical responses to real-life situations.',
            bg: 'bg-[#EDF2F7]'
          }, {
            icon: CheckCircle2Icon,
            title: 'Apply it in daily life',
            text: 'Use tools that are culturally sensitive, faith-rooted and non-political, and easy to apply in daily life.',
            bg: 'bg-[#F7EDE9]'
          }].map((s, i) => <motion.div key={s.title} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            delay: i * 0.08
          }} className={`rounded-2xl border border-ink-900/5 ${s.bg} p-6 shadow-soft`} data-id="element-4034" data-mp="260btzoq4gqhn" data-mpi={i}>
                <span className="w-11 h-11 inline-flex items-center justify-center rounded-full bg-white border border-forest-700/10 text-forest-700" data-id="element-4035" data-mp="3j7xi82r8u51z">
                  <s.icon size={18} strokeWidth={1.6} data-id="element-4036" data-mp="3a93meelqliph" />
                </span>
                <h3 className="mt-5 font-display text-lg text-ink-900" data-id="element-4037" data-mp="359h8s41zy0g5">
                  {s.title}
                </h3>
                <p className="mt-2 text-ink-700 text-sm leading-relaxed" data-id="element-4038" data-mp="30a4dqw0z1n2h">
                  {s.text}
                </p>
              </motion.div>)}
          </div>
        </Container>
      </section>

      {/* Resources you can trust */}
      <section className="py-16 md:py-24 bg-sand-50/60" data-id="element-4039" data-mp="1ktj8qrxzwqt3">
        <Container data-id="element-4040" data-mp="1pq9k51yuuy86">
          <div className="grid lg:grid-cols-2 gap-10" data-id="element-4041" data-mp="1pr18brq4htof">
            <div data-id="element-4042" data-mp="1truyysuy9ml0">
              <h2 className="font-display text-3xl md:text-4xl text-ink-900 leading-tight" data-id="element-4043" data-mp="1tsebtv5qtks6">
                Resources you can trust
              </h2>
              <p className="mt-5 text-ink-700 leading-relaxed" data-id="element-4044" data-mp="x011za4bkdtl">
                Parents receive practical guides, recorded expert talks, family
                conversation tools, and checklists — designed specifically for
                Muslim families living in non-Muslim environments.
              </p>
              <p className="mt-4 text-ink-700 leading-relaxed" data-id="element-4045" data-mp="1av3dqdvukxeb">
                Everything is written to reduce stress, increase clarity, and
                help you respond with confidence — without escalation or
                confrontation.
              </p>
            </div>
            <div data-id="element-4046" data-mp="1llsh5u6ld5lo">
              <div className="rounded-2xl bg-white border border-sage-200 p-6 shadow-soft" data-id="element-4047" data-mp="1gosbhkdnm4v3">
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-4" data-id="element-4048" data-mp="1gmxz4ugg7l2m">
                  All materials are created to be:
                </p>
                <ul className="space-y-2.5 text-ink-700" data-id="element-4049" data-mp="1qfdhhsvnqbo1">
                  {['culturally sensitive', 'non-political', 'easy to apply in daily life', 'safe for children and parents', 'focused on dignity and integration'].map(c => <li key={c} className="flex items-center gap-3" data-id="element-4050" data-mp="17nug1qha75o4">
                      <span className="w-1.5 h-1.5 rounded-full bg-forest-700 flex-shrink-0" data-id="element-4051" data-mp="2n4ujnxvflfkz" />
                      <span data-id="element-4052" data-mp="2j39d0u9hpxj8">{c}</span>
                    </li>)}
                </ul>
              </div>
              <p className="mt-4 text-sm text-ink-500 leading-relaxed" data-id="element-4053" data-mp="2qm8w0yt1hn22">
                We provide education and guidance. We do not provide legal
                representation.
              </p>
            </div>
          </div>
        </Container>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-24 bg-white/60" data-id="element-4054" data-mp="2lnfgl0u6zlz9">
        <Container data-id="element-4055" data-mp="2lo79u50bh7md">
          <div className="grid lg:grid-cols-2 gap-10 items-center" data-id="element-4056" data-mp="2lpigccl5cth6">
            <div data-id="element-4057" data-mp="39drx303i6r1g">
              <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-4058" data-mp="39fcs0qtqcbzu">
                You don't have to figure it out alone
              </h2>
              <p className="mt-5 text-ink-700 leading-relaxed" data-id="element-4059" data-mp="2zmf76e19may2">
                Get ongoing guidance, practical tools, and calm support designed
                for Muslim families raising children in non-Muslim environments.
              </p>
            </div>
            <div className="flex flex-col items-start lg:items-center gap-4" data-id="element-4060" data-mp="xge36vx2541">
              <div className="flex flex-wrap gap-3" data-id="element-4061" data-mp="zj80206wie1">
                <Link to="/en/waitlist" data-id="element-4062" data-mp="29fkbw1yguvuk">
                  <Button variant="primary" data-id="element-4063" data-mp="29cg1unybl5vz">Join Membership</Button>
                </Link>
                <Link to="/en/parents/resources-guidance" data-id="element-4064" data-mp="24e4rmc444td8">
                  <Button variant="secondary" data-id="element-4065" data-mp="1zfjsyh4iehqw">Browse Resources</Button>
                </Link>
              </div>
            </div>
          </div>
        </Container>
      </section>
    </>;
}